"use client";

import { QuickTradeRateAdjust } from "@shared/src/components/modules/quick-trade/QuickTradeRateAdjust";
import { TradeTypeSwitch } from "@shared/src/components/ui/common/buttons/TradeTypeSwitch";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import type { GetQuickRatePayload, QuickRateResult } from "@sdk/shared/types/order.types";
import { useQuery } from "@tanstack/react-query";
import { Form, FormField, FormItem, FormMessage } from "@shared/src/components/ui/form";
import { useWatch, UseFormReturn, useFormState } from "react-hook-form";
import { Button } from "@shared/src/components/ui/button";
import { CustomSelect } from "@shared/src/components/ui/common/form-components/CustomSelect";
import { QuickTradeInputs } from "@shared/src/components/modules/quick-trade/QuickTradeInputs";
import { SecondaryAmountInput } from "@shared/src/components/modules/quick-trade/SecondaryAmountInput";
// import { getSdk } from "@/lib/sdkClient";
import { MIN_QUICK_TRADE_LIMIT } from "@shared/src/utils/constants";
import { IoIosInformationCircle } from "react-icons/io";
import { QuickTradeFormData } from "@shared/src/hooks/quick-trade/useQuickTrade";
import { QuickTradeOptions } from "@shared/src/hooks/quick-trade/useQuickTradeOptions";
import { useHostStore } from "@shared/src/stores/useHostStore";
import { ixToast } from "@shared/src/utils/ixToast";
import { PaymentMethodSelect } from "../../common/misc/PaymentMethodSelect";
import { useQuickTradeFlowStore } from "@shared/src/stores/useQuickTradeFlowStore";
import { cn } from "@shared/src/utils/cn";
import { calculateAmount } from "@sdk/utils";

// const sdk = getSdk();

interface QuickTradeFormProps {
  onSubmit: (data: GetQuickRatePayload, rate: number, paymentConfig: string | undefined) => void;
  form: UseFormReturn<QuickTradeFormData>;
  options: QuickTradeOptions;
}

export function QuickTradeForm({ onSubmit, form, options }: QuickTradeFormProps) {
  const sdk = useHostStore(state => state.sdk);
  const setAndSwitchChain = useHostStore(state => state.setAndSwitchChain);
  const paymentSelectorRef = useRef<HTMLDivElement>(null);
  const [manualRate, setManualRate] = useState<number | null>(null);
  const [isPrimaryCrypto, setIsPrimaryCrypto] = useState(true); // Track which input is primary
  const isProgressiveReveal = true;

  // Handler to swap primary and secondary inputs
  const handleSwap = () => {
    setIsPrimaryCrypto(!isPrimaryCrypto);
  };

  const {
    networkOptions,
    paymentOptions,
    currencyOptions,
    tokenOptions,
    getTokensForNetwork,
    isLoading: optionsLoading,
    error: optionsError,
  } = options;

  console.log("QuickTradeForm render 1", {
    optionsLoading,
    optionsError,
    networkOptions,
    paymentOptions,
    currencyOptions,
    tokenOptions,
  });

  // Get current form values - watch individual fields to prevent excessive re-renders
  const tradeType = useWatch({ control: form.control, name: "tradeType" });
  const amount = useWatch({ control: form.control, name: "amount" });
  const paymentMethodId = useWatch({ control: form.control, name: "paymentMethod" });
  const paymentMethod = sdk?.config?.paymentMethods?.find(pm => pm.id === paymentMethodId);
  const paymentConfig = useWatch({ control: form.control, name: "paymentConfig" });
  const network = useWatch({ control: form.control, name: "network" });
  const fiatCurrency = useWatch({ control: form.control, name: "fiatCurrency" });
  const token = useWatch({ control: form.control, name: "token" });
  const formState = useFormState({ control: form.control });
  const [needsReestimate, setNeedsReestimate] = useState(false);
  const headerHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { activeChain } = useHostStore();

  useEffect(() => {
    if (activeChain) {
      console.log("activeChainform", activeChain);
      form.setValue("network", activeChain.id);
    }
  }, [activeChain, form]);

  // Create payload helper function
  const createPayload = useCallback(() => {
    console.log("createPayload", { amount, fiatCurrency, network, paymentMethod: paymentMethodId, token, tradeType });
    if (!paymentMethodId || !amount || !token || !fiatCurrency || !tradeType || !network) {
      return null;
    }

    const networkTokens = getTokensForNetwork(network);
    const selectedToken = networkTokens.find(t => t.value === token);
    if (!selectedToken) return null;

    const tokenAddress = selectedToken.value.split("-")[0];

    return {
      tokenAddress,
      currency: fiatCurrency,
      traderChain: Number(network),
      quantity: Number(amount),
      paymentMethod: paymentMethodId,
      type: tradeType,
    };
  }, [amount, fiatCurrency, getTokensForNetwork, network, paymentMethodId, token, tradeType]);

  const [payload, setPayload] = useState<GetQuickRatePayload | null>();

  console.log("QuickTradeForm render 2", {
    optionsLoading,
    optionsError,
    networkOptions,
    paymentOptions,
    currencyOptions,
    tokenOptions,
  });

  // Rate query
  const {
    data: quickRateData,
    isError: quickRateError,
    isFetching: quickRateLoading,
  } = useQuery<QuickRateResult | null>({
    queryKey: ["quickRate", payload],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 10));
      if (!payload) return null;
      const res = await sdk?.orders.getQuickRate(payload);
      if (res?.error) throw res.error;
      if (!res?.data?.rate) throw new Error("No ads found");
      return res.data;
    },
    enabled: !!payload,
    select: data => data ?? null,
  });
  const quickRate = quickRateData?.bestRate;

  const tokenSymbol = tokenOptions.find(t => t.value === token)?.label.split(" ")[0] || "tokens";

  useEffect(() => {
    if (amount && Number(amount) < MIN_QUICK_TRADE_LIMIT) {
      let toast: ReturnType<typeof ixToast.error>;
      const id = setTimeout(() => {
        toast = ixToast.error(`The minimum trade amount is ${MIN_QUICK_TRADE_LIMIT} ${tokenSymbol}`, {
          title: "Amount Too Low",
        });
      }, 1000);
      return () => {
        clearTimeout(id);
        if (toast) {
          toast.dismiss();
        }
      };
    }
  }, [amount]);

  const effectiveRate = manualRate || quickRate || 0;
  const [secondaryAmount, setSecondaryAmount] = useState(calculateAmount(amount, effectiveRate, true));
  // Memoized derived values
  const step1Complete = isProgressiveReveal ? Boolean(amount && Number(amount) >= MIN_QUICK_TRADE_LIMIT) : true;
  const showPaymentSelector = isProgressiveReveal
    ? Boolean(step1Complete || paymentMethodId || !isPrimaryCrypto)
    : true;
  console.log("showPaymentSelector", { showPaymentSelector, secondaryAmount, isPrimaryCrypto });
  const showRateAdjuster = isProgressiveReveal ? Boolean(paymentMethodId) : true;
  const showSecondaryInput = useMemo(
    () =>
      isProgressiveReveal
        ? Boolean(paymentMethodId && paymentMethodId !== "" && quickRate !== null && !quickRateLoading)
        : true,
    [paymentMethodId, quickRate, quickRateLoading],
  );
  const showAmountInput = isProgressiveReveal ? showSecondaryInput && (quickRate || manualRate) : true;
  const showPaymentConfig =
    showSecondaryInput && sdk?.auth.status === "authenticated" && !!paymentMethod && tradeType === "sell";

  const [headerVisibility, setHeaderVisibility] = useState<"visible" | "hiding">(
    showSecondaryInput ? "hiding" : "visible",
  );

  useEffect(() => {
    if (!quickRateData && showRateAdjuster) {
      setPayload(createPayload());
    }
  }, [createPayload, quickRateData, showRateAdjuster]);

  useEffect(() => {
    setPayload(createPayload());
  }, [tradeType, paymentMethodId, token, fiatCurrency, network]);

  useEffect(() => {
    setNeedsReestimate(true);
  }, [createPayload]);

  useEffect(() => {
    if (showSecondaryInput && headerVisibility === "visible") {
      setHeaderVisibility("hiding");
    }
  }, [showSecondaryInput, headerVisibility]);

  useEffect(() => {
    return () => {
      if (headerHideTimerRef.current) {
        clearTimeout(headerHideTimerRef.current);
      }
    };
  }, []);

  // Reset crypto currency when network changes
  useEffect(() => {
    if (optionsLoading || !network) return;

    const networkTokens = getTokensForNetwork(network);

    const tokenName = sdk?.config?.tokens?.find(t => t.address === token.split("-")[0])?.symbol;

    const isCurrentCryptoValid = token && networkTokens.some(t => t.value === token);

    if (!token || !isCurrentCryptoValid) {
      console.log("resetting token for network", { network, token, networkTokens, tokenName });
      if (networkTokens.length > 0) {
        const defaultToken = networkTokens.find(token => token.label === tokenName) || networkTokens[0];
        form.setValue("token", defaultToken?.value || "");
      }
    }
  }, [network, optionsLoading, getTokensForNetwork, form, token]);

  // Event handlers
  const handleValidFormSubmit = (data: QuickTradeFormData, formErrors) => {
    console.log("handleFormSubmit", { data, quickRate });
    if (!quickRate) throw new Error("Rate is not set");

    const payload = createPayload();
    if (!payload) {
      console.error("Failed to create payload");
      return;
    }

    onSubmit(payload, quickRate, data.paymentConfig);
  };

  const handleFormError = errors => {
    console.log("handleFormError", { errors });
  };

  const handleReestimate = () => {
    setPayload(createPayload());
    setNeedsReestimate(false);
    setManualRate(null);
  };

  // Loading state
  if (optionsLoading) {
    return (
      <div className="flex flex-col items-center w-full min-h-screen px-4 py-8">
        <header className="text-center w-full max-w-lg mb-8">
          <h3 className="font-medium mb-2 text-text-primary">
            <span className="text-text-brand">Quick</span> Trade
          </h3>
          <p>
            Easily trade cryptocurrencies directly from your decentralized wallet using a variety of payment options.
          </p>
        </header>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-text-brand"></div>
          <span className="ml-3 text-text-secondary">Loading options...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (optionsError) {
    return (
      <div className="flex flex-col items-center w-full min-h-screen px-4 py-2">
        <header className="text-center w-full max-w-lg mb-8">
          <h3 className="font-medium mb-2 text-text-primary">
            <span className="text-text-brand">Quick</span> Trade
          </h3>
          <p>
            Easily trade cryptocurrencies directly from your decentralized wallet using a variety of payment options.
          </p>
        </header>
        <div className="flex flex-col items-center justify-center py-8 text-text-error">
          <p className="mb-4">Failed to load trading options</p>
          <p className="text-sm text-text-secondary">{optionsError.message}</p>
        </div>
      </div>
    );
  }

  // if (!tradeType || !token || !fiatCurrency || !network) {
  //   return null;
  // }

  const handleManualRateChange = (newRate: number) => {
    setManualRate(newRate);
  };

  const handleNetworkChange = async (value: string | string[]) => {
    console.log("handleNetworkChange", { value });
    const res = await setAndSwitchChain(Number(value));
    console.log("handleNetworkChange res", res);
    if (value && typeof value === "string") {
      form.setValue("network", Number(value));
      form.clearErrors("network");
    }
  };

  function handleSubmit(e) {
    e.preventDefault();
    if (sdk?.auth.status !== "authenticated") {
      console.log("User not authenticated, showing sign in modal", sdk);
      ixToast.error("Please sign in to continue");
      useQuickTradeFlowStore.getState().setShowSignInModal(true);
      return;
    }
    form.handleSubmit(handleValidFormSubmit, handleFormError)(e);
  }

  // ========================================
  // Compute input components beforehand
  // ========================================

  // Crypto input (primary or secondary based on isPrimaryCrypto)
  const cryptoInput = (
    <div className="w-full flex flex-col gap-1">
      <FormField
        control={form.control}
        name="amount"
        render={({ field }) => (
          <FormItem>
            <QuickTradeInputs
              tradeType={tradeType}
              amount={amount || ""}
              setAmount={value => {
                field.onChange(value);
              }}
              selectedNetwork={network.toString()}
              handleNetworkChange={handleNetworkChange}
              networkOptions={networkOptions}
              selectedFiatCurrency={fiatCurrency}
              handleFiatCurrencyChange={() => {}}
              currencyOptions={currencyOptions}
              selectedToken={token}
              handleTokenChange={value => {
                if (typeof value === "string") {
                  form.setValue("token", value);
                  form.clearErrors("token");
                }
              }}
              cryptoOptions={getTokensForNetwork(network)}
              isPrimary={isPrimaryCrypto}
              onSwap={handleSwap}
              error={
                typeof formState.errors.amount === "object" && formState.errors.amount?.message
                  ? String(formState.errors.amount.message)
                  : undefined
              }
            />
          </FormItem>
        )}
      />
    </div>
  );

  // Fiat input (primary or secondary based on isPrimaryCrypto)
  const fiatInput = (
    <div
      className={!showAmountInput && !isPrimaryCrypto ? "" : "animate-in slide-in-from-top-5 fade-in-0 duration-300"}
    >
      <SecondaryAmountInput
        onChangeSecondary={setSecondaryAmount}
        secondaryAmount={secondaryAmount}
        tradeType={tradeType}
        amount={amount || ""}
        setAmount={value => {
          form.setValue("amount", value);
          form.trigger("amount");
        }}
        fiatCurrency={fiatCurrency}
        currencyOptions={currencyOptions}
        handleFiatCurrencyChange={value => {
          if (typeof value === "string") {
            form.setValue("fiatCurrency", value);
            form.clearErrors("fiatCurrency");
          }
        }}
        paymentMethod={paymentMethodId}
        paymentOptions={paymentOptions}
        handlePaymentMethodChange={value => {
          if (typeof value === "string") {
            form.setValue("paymentMethod", value);
            form.clearErrors("paymentMethod");
          }
        }}
        rate={effectiveRate}
        isPrimary={!isPrimaryCrypto}
        onSwap={handleSwap}
      />
    </div>
  );

  // Currency and payment method selectors
  const currencyAndPaymentSelectors = (
    <div ref={paymentSelectorRef} className="animate-in slide-in-from-top-5 fade-in-0 duration-300">
      <div className="flex gap-4">
        <div className="flex-1">
          <FormField
            control={form.control}
            name="fiatCurrency"
            render={({ field }) => (
              <FormItem>
                <CustomSelect
                  label="Currency"
                  value={fiatCurrency}
                  onValueChange={value => {
                    if (typeof value === "string") {
                      field.onChange(value);
                      form.clearErrors("fiatCurrency");
                    }
                  }}
                  options={currencyOptions}
                  placeholder="Select currency"
                  containerClassName="w-full"
                  error={!!formState.errors.fiatCurrency}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex-[2]">
          <FormField
            control={form.control}
            name="paymentMethod"
            render={({ field }) => (
              <FormItem>
                <CustomSelect
                  label="Choose Payment Method"
                  value={paymentMethodId}
                  onValueChange={value => {
                    if (typeof value === "string") {
                      field.onChange(value);
                      form.clearErrors("paymentMethod");
                    }
                  }}
                  options={paymentOptions}
                  placeholder="Select payment method"
                  containerClassName="w-full"
                  error={!!formState.errors.paymentMethod}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );

  // Determine which input should be shown first and second
  const primaryInput = isPrimaryCrypto ? cryptoInput : fiatInput;
  const secondaryInput = isPrimaryCrypto ? fiatInput : cryptoInput;

  // ========================================
  // End of computed components
  // ========================================

  return (
    <div className="flex flex-col items-center w-full">
      {
        <header
          aria-hidden={headerVisibility !== "visible"}
          className={cn(
            "text-center w-full max-w-lg transition-all duration-1000 ease-in-out transform",
            headerVisibility === "visible"
              ? "opacity-100 translate-y-0 mb-8 max-h-48"
              : "opacity-0 -translate-y-6 pointer-events-none mb-0 max-h-0 overflow-hidden",
          )}
        >
          <h3 className="font-medium mb-2 text-text-primary">
            <span className="text-text-brand">Quick</span> Trade
          </h3>
          <p>
            Easily trade cryptocurrencies directly from your decentralized wallet using a variety of payment options.
          </p>
        </header>
      }
      <Form {...form}>
        <form onSubmit={handleSubmit} className="w-full max-w-lg space-y-4">
          <div className="w-fit mx-auto">
            <TradeTypeSwitch
              value={tradeType}
              onToggle={value => form.setValue("tradeType", value)}
              activeColor="bg-surfaces-bg-secondary"
            />
          </div>

          {/* Primary Input (Crypto or Fiat based on isPrimaryCrypto) */}
          {primaryInput}

          {/* Currency and Payment Method Selectors */}
          {showPaymentSelector && currencyAndPaymentSelectors}

          {/* Secondary Input (Fiat or Crypto based on isPrimaryCrypto) */}
          {showSecondaryInput && secondaryInput}

          {/* Rate Adjustment */}
          {showRateAdjuster && (
            <div className="animate-in slide-in-from-top-5 fade-in-0 duration-300">
              <QuickTradeRateAdjust
                rate={quickRate || null}
                isLoading={quickRateLoading}
                noAds={!!payload && !quickRateData?.numAds && !quickRateLoading}
                manualRate={manualRate}
                onManuRateChange={handleManualRateChange}
                adsCount={quickRateData && "numAds" in quickRateData ? quickRateData.numAds ?? 0 : 0}
                onAdjustOptions={() => {}}
                needsReestimate={needsReestimate}
                onReestimate={handleReestimate}
              />
            </div>
          )}

          {/* Step 5: Payment Method Selection */}
          {showPaymentConfig && (
            <div ref={paymentSelectorRef} className="animate-in slide-in-from-top-5 fade-in-0 duration-300">
              <div className="flex gap-4">
                <div className="flex-[2]">
                  <FormField
                    control={form.control}
                    name="paymentConfig"
                    render={({ field }) => (
                      <FormItem>
                        <PaymentMethodSelect
                          label={"Payment details"}
                          value={field.value}
                          onValueChange={field.onChange}
                          tradeType={tradeType}
                          adPaymentMethods={[paymentMethod]}
                          placeholder={"Provide Payment details for receiving payment"}
                          containerClassName="w-full"
                          error={!!form.formState.errors.paymentConfig}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Find Merchant Button */}
          {showAmountInput && (
            <div className="animate-in mt-2 slide-in-from-top-3 fade-in-0 duration-200 delay-550">
              <Button
                type="submit"
                variant={tradeType === "buy" ? "success" : "danger"}
                size="lg"
                radius="full"
                width="full"
                className="capitalize disabled:opacity-50"
                disabled={!quickRateData?.numAds || !showSecondaryInput || !paymentMethod}
              >
                Find Merchant
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}
